import React, { useState, useCallback, useEffect } from 'react';
import { CircularProgress, Scrollbar, Typography, Alert } from '@ann/web-components';
import {
  KnowledgePageContainer,
  ContentContainer,
  GridContainer,
  ListContainer,
  EmptyState,
  LoadingContainer,
} from './KnowledgePage.styled';
import { KnowledgeHeader, DriveCard, DocumentCard, SlideoutDrives, SlideoutDriveDetail, SlideoutDocumentDetail, DriveListItem, DocumentListItem, RemoveDriveAccessModal, RemoveDocumentAccessModal, DeleteDocumentModal } from './components';
import { useKnowledgePage, useFileUpload } from './hooks';

const KnowledgePage = () => {
  const {
    activeTab,
    viewMode,
    searchTerm,
    drives,
    agentDocuments,
    vaultDocuments,
    loading,
    error,
    totalDrivesTabItems,
    totalDocumentsTabItems,
    handleTabChange,
    handleViewModeChange,
    handleSearchChange,
    handleDriveClick,
    handleRemoveDriveAccess,
    handleAgentDocumentClick,
    handleDeleteAgentDocument,
    handleVaultDocumentClick,
    handleDeleteVaultDocument,
    removingDocumentIds,
    deletingDocumentIds,
    removingDriveIds,
    selectedDrive,
    selectedDocument,
    showDriveDetail,
    showDocumentDetail,
    handleCloseDriveDetail,
    handleCloseDocumentDetail,
    showRemoveDriveModal,
    showRemoveDocumentModal,
    showDeleteDocumentModal,
    itemToRemove,
    handleConfirmRemoveDrive,
    handleConfirmRemoveDocument,
    handleConfirmDeleteDocument,
    handleCloseRemoveDriveModal,
    handleCloseRemoveDocumentModal,
    handleCloseDeleteDocumentModal,
  } = useKnowledgePage();

  // Hook de subida de archivos - cambiar a tab 1 (Documents) cuando se suben archivos
  const {
    fileInputRef,
    isUploading,
    openFilePicker,
    handleFileSelect,
    uploadErrors,
    clearErrors,
  } = useFileUpload({ onUploadStart: () => handleTabChange(1) });

  // Estado de alertas
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');

  // Mostrar alerta cuando hay errores de upload
  useEffect(() => {
    if (uploadErrors.length > 0) {
      // Crear mensaje combinado de todos los errores
      const errorMessages = uploadErrors.map(err => {
        return `${err.fileName}: ${err.message}`;
      }).join('\n');
      
      setAlertMessage(
        uploadErrors.length === 1
          ? errorMessages
          : `${uploadErrors.length} files failed to upload:\n${errorMessages}`
      );
      setAlertType('error');
      setShowAlert(true);
    }
  }, [uploadErrors]);

  // Handler para cerrar alerta
  const handleCloseAlert = useCallback(() => {
    setShowAlert(false);
    setAlertMessage('');
    clearErrors();
  }, [clearErrors]);

  // Total items para el header
  const totalItems = activeTab === 0 ? totalDrivesTabItems : totalDocumentsTabItems;

  // Estado del slideout de drives
  const [showSlideoutDrives, setShowSlideoutDrives] = useState(false);

  // Handler para browse drives
  const handleBrowseDrives = useCallback(() => {
    setShowSlideoutDrives(true);
  }, []);

  const handleCloseSlideoutDrives = useCallback(() => {
    setShowSlideoutDrives(false);
  }, []);

  // Render del contenido para Tab 0 (Drives + Agent Documents)
  const renderDrivesTabContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <CircularProgress size="xl" />
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <EmptyState>
          <Typography category="body" size="medium" color="body">
            Error loading data. Please try again.
          </Typography>
        </EmptyState>
      );
    }

    const totalItems = drives.length + agentDocuments.length;

    if (totalItems === 0) {
      return (
        <EmptyState>
          <Typography category="subtitle" size="medium" weight="bold">
            No drives or documents found
          </Typography>
          <Typography category="body" size="medium" color="body">
            Add drives or documents to give your agent access to knowledge.
          </Typography>
        </EmptyState>
      );
    }

    const Container = viewMode === 'grid' ? GridContainer : ListContainer;

    return (
      <Scrollbar size="small">
        <Container>
          {/* Primero renderizamos los drives */}
          {drives.map((drive) => 
            viewMode === 'grid' ? (
              <DriveCard
                key={drive.vaultUuid || drive.uuid || drive.id}
                drive={drive}
                onClick={handleDriveClick}
                onRemoveAccess={handleRemoveDriveAccess}
              />
            ) : (
              <DriveListItem
                key={drive.vaultUuid || drive.uuid || drive.id}
                drive={drive}
                onClick={handleDriveClick}
                onRemoveAccess={handleRemoveDriveAccess}
              />
            )
          )}
          {/* Luego renderizamos los documentos asignados al agente */}
          {agentDocuments.map((doc) => 
            viewMode === 'grid' ? (
              <DocumentCard
                key={doc.uuid || doc.id}
                document={doc}
                onClick={handleAgentDocumentClick}
                onAction={handleDeleteAgentDocument}
                actionLabel="Remove access"
                isRemoving={removingDocumentIds.has(doc.uuid || doc.id)}
              />
            ) : (
              <DocumentListItem
                key={doc.uuid || doc.id}
                document={doc}
                onClick={handleAgentDocumentClick}
                onAction={handleDeleteAgentDocument}
                actionLabel="Remove access"
                isRemoving={removingDocumentIds.has(doc.uuid || doc.id)}
              />
            )
          )}
        </Container>
      </Scrollbar>
    );
  };

  // Render del contenido para Tab 1 (Vault Documents)
  const renderDocumentsTabContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <CircularProgress size="xl" />
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <EmptyState>
          <Typography category="body" size="medium" color="body">
            Error loading documents. Please try again.
          </Typography>
        </EmptyState>
      );
    }

    if (vaultDocuments.length === 0) {
      return (
        <EmptyState>
          <Typography category="subtitle" size="medium" weight="bold">
            No documents in agent vault
          </Typography>
          <Typography category="body" size="medium" color="body">
            Upload documents to your agent's vault to build its knowledge base.
          </Typography>
        </EmptyState>
      );
    }

    const Container = viewMode === 'grid' ? GridContainer : ListContainer;

    return (
      <Scrollbar size="small">
        <Container>
          {vaultDocuments.map((doc) => 
            viewMode === 'grid' ? (
              <DocumentCard
                key={doc.uuid || doc.id}
                document={doc}
                onClick={handleVaultDocumentClick}
                onAction={handleDeleteVaultDocument}
                actionLabel="Delete"
                isRemoving={deletingDocumentIds.has(doc.uuid || doc.id)}
              />
            ) : (
              <DocumentListItem
                key={doc.uuid || doc.id}
                document={doc}
                onClick={handleVaultDocumentClick}
                onAction={handleDeleteVaultDocument}
                actionLabel="Delete"
                isRemoving={deletingDocumentIds.has(doc.uuid || doc.id)}
              />
            )
          )}
        </Container>
      </Scrollbar>
    );
  };

  return (
    <>
      <KnowledgePageContainer>
        <KnowledgeHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onBrowseDrives={handleBrowseDrives}
          onUploadClick={openFilePicker}
          totalItems={totalItems}
          fileInputRef={fileInputRef}
          onFileSelect={handleFileSelect}
          isUploading={isUploading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />
        <ContentContainer>
          {activeTab === 0 ? renderDrivesTabContent() : renderDocumentsTabContent()}
        </ContentContainer>
      </KnowledgePageContainer>

      <SlideoutDrives
        open={showSlideoutDrives}
        onClose={handleCloseSlideoutDrives}
        onAssignSuccess={() => handleTabChange(0)}
      />

      <SlideoutDriveDetail
        open={showDriveDetail}
        onClose={handleCloseDriveDetail}
        drive={selectedDrive}
      />

      <SlideoutDocumentDetail
        open={showDocumentDetail}
        onClose={handleCloseDocumentDetail}
        documentData={selectedDocument}
      />

      <RemoveDriveAccessModal
        isOpen={showRemoveDriveModal}
        onClose={handleCloseRemoveDriveModal}
        onConfirm={handleConfirmRemoveDrive}
        drive={itemToRemove}
        isLoading={itemToRemove && removingDriveIds.has(itemToRemove.vaultUuid || itemToRemove.uuid || itemToRemove.id)}
      />

      <RemoveDocumentAccessModal
        isOpen={showRemoveDocumentModal}
        onClose={handleCloseRemoveDocumentModal}
        onConfirm={handleConfirmRemoveDocument}
        document={itemToRemove}
        isLoading={itemToRemove && removingDocumentIds.has(itemToRemove.uuid || itemToRemove.id)}
      />

      <DeleteDocumentModal
        isOpen={showDeleteDocumentModal}
        onClose={handleCloseDeleteDocumentModal}
        onConfirm={handleConfirmDeleteDocument}
        document={itemToRemove}
        isLoading={itemToRemove && deletingDocumentIds.has(itemToRemove.uuid || itemToRemove.id)}
      />

      {/* Alerta de errores de upload */}
      {showAlert && (
        <Alert
          severity={alertType}
          title={alertType === 'error' ? 'Upload Failed' : 'Upload Status'}
          description={alertMessage}
          onClose={handleCloseAlert}
          maxWidth="500px"
          closable={true}
          autoClose={true}
          autoCloseDelay={8000}
        />
      )}
    </>
  );
};

export default KnowledgePage;
